import React, { useState, useEffect } from 'react';
import { DeleteIcon, DirectoryIcon, DragIcon, InformationIcon, LinkIcon } from 'src/images';
import styles from './knowledge.module.scss';
import classnames from 'classnames';
import { FavoritesButton } from 'src/components/favorites-button';
import { UpdatedIndication } from 'src/components/updatedIndication';
import { useDrag, useDrop } from 'react-dnd';
import { SingleTagIcon } from 'src/components/tags/tagsIcons';
import analyticsService from 'src/helpers/analytics.service';
import {
  EVENTS,
  KNOWLEDGE_TYPES,
  KNOWLEDGE_ITEM,
  DATA_KEYS,
  Lang,
  ROUTES,
  FAVORITES_ENTITIES,
} from 'shared/constants';
import { Input } from 'antd';
import { ShareBoxComponent } from 'src/components/share';
import { IconButton } from 'src/components/design-system/buttons';
import { AttachmentsBadge } from 'src/components/design-system/attachments-badge';
import { ExtendExpirationButtons } from 'src/components/knowledge-content/knowledge-content-editor/extend-expiration-buttons.component';
import { LabelsList } from 'src/components/labels';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { ItemType } from 'src/components/core/knowledge';
import { Link } from 'react-router-dom';

const { TextArea } = Input;

const Knowledge = React.memo(function Knowledge({
  id,
  title = '',
  subtitle = '',
  link = '',
  thumbnail = '',
  ownerIds = [],
  itemUpdatedAt = null,
  light = false,
  adminMode = false,
  deleteKnowledge = () => {},
  moveKnowledgeAction = null,
  dragItem = () => {},
  findItem = () => {},
  dndGroup = '',
  draggable = true,
  finishDrag = () => {},
  tags,
  showSpecialty = false,
  actionButtons = null,
  openInformation = () => {},
  linked = false,
  externalUpdateAction = () => {},
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
  type = KNOWLEDGE_TYPES.LINK,
  expiresAt,
  allowEditExpired,
  showLabels = false,
  labels,
  onClickEvent = () => {},
  hideFavorites = false,
  hideInfo = false,
  basePath = ROUTES.KNOWLEDGE_ITEMS,
  preventOpen = false,
  observeRef,
  isSourceDerived = false,
  readOnly = false,
  attachments = [],
  attachmentsCount = 0,
  dragInGroupActive = false,
}) {
  let boxRef = React.useRef(null);

  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  const [titleInput, setTitleInput] = useState(title);
  const [subtitleInput, setSubtitleInput] = useState(subtitle);

  useEffect(() => {
    if (titleInput !== title) {
      setTitleInput(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, setTitleInput]);

  useEffect(() => {
    if (subtitleInput !== subtitle) {
      setSubtitleInput(subtitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtitle, setSubtitleInput]);

  const originalIndex = findItem(id)?.index;

  const [{ isDragging }, drag] = useDrag({
    item: { type: dndGroup, id, originalIndex },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => adminMode && draggable && !isSourceDerived && !readOnly,
    end: (dropResult, monitor) => {
      const { id: droppedId, originalIndex } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        dragItem(droppedId, originalIndex);
      }

      finishDrag(droppedId);
    },
  });
  // Hovering over a source-derived sibling snaps the dragged item back to its
  // original index so it never appears reordered while the cursor is over the
  // non-droppable area.
  const [, drop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId, originalIndex: draggedOriginalIndex }) {
      if (isSourceDerived) {
        dragItem(draggedId, draggedOriginalIndex);
        return;
      }
      if (draggedId !== id) {
        const { index: overIndex } = findItem(id);
        dragItem(draggedId, overIndex);
      }
    },
  });

  // While a sibling is being dragged in this dnd group, source-derived items dim
  // to signal they aren't a valid drop target. The list watches the drag layer once.
  const dimmedAsSourceDerived = isSourceDerived && dragInGroupActive;

  if (draggable) {
    boxRef = drag(drop(boxRef));
  }

  const onClick = () => {
    if (preventOpen) {
      return;
    }

    const event = {
      id: id.toString(),
      title,
      ownerIds,
      type,
      ...tags,
    };

    onClickEvent(event);
  };

  const openInformationInner = React.useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      openInformation(e);

      analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.OPEN_INFORMATION, {
        id: id.toString(),
        title,
        ownerIds,
        type,
        ...tags,
      });
    },
    [id, tags, title, ownerIds, type, openInformation],
  );

  const deleteKnowledgeItem = React.useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      deleteKnowledge({ id, title });
    },
    [deleteKnowledge, id, title],
  );

  const changeDirectory = React.useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      moveKnowledgeAction({ id, title, ownerIds, thumbnail });
    },
    [id, moveKnowledgeAction, title, ownerIds, thumbnail],
  );

  const onInputFocus = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // TODO: add analytics
      e.target.blur();
    }
  };

  const onBlur = async e => {
    let { value, name: field } = e.target;
    // TODO: add analytics
    if ((field === 'title' && value === title) || (field === 'subtitle' && value === subtitle)) {
      return false;
    }
    await externalUpdateAction({ [field]: value });
  };

  const onChange = e => {
    if (e.target.name === 'title') {
      setTitleInput(e.target.value);
    }
    if (e.target.name === 'subtitle') {
      setSubtitleInput(e.target.value);
    }
  };

  const renderThumbnail = () => {
    if (!thumbnail) {
      return <ItemType type={type} link={link} className={styles.itemIcon} />;
    }

    return (
      <div className={styles.thumbnailWrapper}>
        <img src={thumbnail} alt={title} className={styles.thumbnail} />
      </div>
    );
  };

  const renderTagIcon = () => {
    let tagToRender = showSpecialty ? tags?.specialty : null;
    if (!tagToRender) return null;

    return <SingleTagIcon tag={tagToRender} darkBackground={!showSpecialty} />;
  };

  const renderInfoButton = () => {
    if (hideInfo) return null;

    return (
      <IconButton
        data-key={DATA_KEYS.KNOWLEDGE_BASE.INFO}
        onClick={openInformationInner}
        className={styles.buttonWrapper}
        label={lang.INFO}>
        <InformationIcon className={styles.informationIcon} />
      </IconButton>
    );
  };

  const renderActions = () => {
    const knowledgeItemLink = basePath.replace(':id', id);
    const renderShareBox = (
      <ShareBoxComponent
        path={knowledgeItemLink}
        iconClassName={styles.actionIcon}
        id={id}
        title={title}
        tags={tags}
        type={type}
        entityType={KNOWLEDGE_ITEM}
        className={styles.buttonWrapper}
      />
    );

    if (!adminMode) {
      return (
        <>
          <UpdatedIndication lastUpdate={itemUpdatedAt} />
          <div className={styles.buttons} role="group" aria-label="Actions">
            {renderTagIcon()}
            {renderShareBox}
            {renderInfoButton()}
            {!hideFavorites && (
              <FavoritesButton
                id={id}
                className={styles.actionIcon}
                wrapper={styles.buttonWrapper}
                entity={FAVORITES_ENTITIES.KNOWLEDGE}
              />
            )}
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles.buttons} role="group" aria-label="Actions">
          {actionButtons ? (
            <>
              {renderShareBox}
              {renderInfoButton()}
              {actionButtons()}
            </>
          ) : (
            <>
              <IconButton
                data-key={DATA_KEYS.KNOWLEDGE_BASE.DELETE}
                onClick={deleteKnowledgeItem}
                className={styles.buttonWrapper}
                label={lang.DELETE}>
                <DeleteIcon className={styles.deleteIcon} />
              </IconButton>
              {!!moveKnowledgeAction && (
                <IconButton
                  onClick={changeDirectory}
                  className={styles.buttonWrapper}
                  label={lang.MOVE}>
                  <DirectoryIcon className={styles.changeDirectory} />
                </IconButton>
              )}
            </>
          )}
          {draggable && (
            <IconButton
              label={lang.DRAG}
              className={classnames(styles.buttonWrapper, styles.dragIconWrapper)}
              /* Drag handle is hidden from keyboard navigation and screen readers
                since drag-and-drop is not accessible - users can reorder via other means */
              tabIndex={-1} //
              aria-hidden="true">
              <DragIcon className={styles.dragIcon} />
            </IconButton>
          )}
        </div>
      </>
    );
  };

  const renderText = () => {
    if (adminMode && !linked) {
      return (
        <>
          <TextArea
            onClick={onInputFocus}
            onKeyDown={onPress}
            onBlur={onBlur}
            className={styles.titleTextarea}
            autoSize={{ minRows: 1 }}
            disabled={!adminMode || !hasEditPermission || readOnly}
            value={titleInput}
            onChange={onChange}
            name="title"
          />
          <TextArea
            onClick={onInputFocus}
            onKeyDown={onPress}
            onBlur={onBlur}
            className={styles.subtitleTextarea}
            autoSize={{ minRows: 1 }}
            disabled={!adminMode || !hasEditPermission || readOnly}
            value={subtitleInput}
            onChange={onChange}
            name="subtitle"
          />
        </>
      );
    }

    return (
      <>
        <h4>{title}</h4>
        <h5 className={styles.subtitle}>{subtitle}</h5>
        <div aria-hidden="true" aria-label="Location list" className={styles.labelsContainer}>
          {showLabels && <LabelsList labels={labels} />}
        </div>
      </>
    );
  };

  const extendExpirationAction = async value => {
    await externalUpdateAction({ expiresAt: value });
  };

  let opacity = 1;
  if (isDragging) {
    opacity = 0;
  } else if (dimmedAsSourceDerived) {
    opacity = 0.5;
  }

  return (
    <div className={styles.wrapper} ref={el => observeRef?.(el, id, type)}>
      <Link
        ref={boxRef}
        style={{ opacity }}
        className={classnames(styles.root, {
          [styles.light]: light,
          [styles.preventOpen]: preventOpen,
        })}
        to={basePath.replace(':id', id)}
        onClick={onClick}>
        {renderThumbnail()}
        <div className={styles.content}>
          {adminMode && linked && (
            <img src={LinkIcon} className={styles.linkIcon} alt="Linked item" aria-hidden="true" />
          )}
          {renderText()}
          <AttachmentsBadge
            count={attachments?.length || attachmentsCount}
            className={styles.attachmentsBadge}
          />
        </div>

        {renderActions()}
      </Link>
      {allowEditExpired && (
        <ExtendExpirationButtons
          expiresAt={expiresAt}
          onExtendExpiration={extendExpirationAction}
          showExpirationMessage
          showToastMessage
          // Nothing to extend without a stored date — and the success toast fires
          // regardless of the no-op, so block the click rather than rely on the caller.
          disabled={!expiresAt}
        />
      )}
    </div>
  );
});

export { Knowledge };
